import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Spinner } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { AiProviderId, AiProviderSettings, AiProviderStatus } from '../types';

const providerLabels: Record<AiProviderId, string> = {
    GEMINI: 'Google Gemini',
    CLAUDE: 'Anthropic Claude',
};

/**
 * Switches which vision model produces diagnoses. Takes effect on the next analysis — the
 * backend stores the choice, so it survives a redeploy.
 */
const AiProviderCard: React.FC = () => {
    const [settings, setSettings] = useState<AiProviderSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<AiProviderId | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get<AiProviderSettings>('/admin/ai-provider');
            setSettings(response.data);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось загрузить настройки ИИ');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const select = async (provider: AiProviderId) => {
        setSaving(provider);
        setError(null);
        setNotice(null);
        try {
            const response = await api.put<AiProviderSettings>('/admin/ai-provider', { provider });
            setSettings(response.data);
            setNotice(`Диагностика переключена на ${providerLabels[provider]}. Применяется со следующего анализа.`);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось переключить провайдера');
        } finally {
            setSaving(null);
        }
    };

    return (
        <Card className="shadow-sm mb-4">
            <Card.Body>
                <Card.Title><i className="bi bi-cpu text-primary me-2" />Модель для диагностики</Card.Title>
                <div className="muted-small mb-3">
                    Какая модель формирует заключение. Переключение вступает в силу со следующего анализа,
                    перезапуск сервера не нужен.
                </div>

                {error && <Alert variant="danger" className="py-2">{error}</Alert>}
                {notice && <Alert variant="success" className="py-2">{notice}</Alert>}

                {loading ? (
                    <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : (
                    <div className="d-flex flex-wrap gap-3">
                        {settings?.providers.map((p: AiProviderStatus) => (
                            <Card
                                key={p.provider}
                                className={p.active ? 'border-primary flex-grow-1' : 'flex-grow-1'}
                                style={{ minWidth: 260 }}
                            >
                                <Card.Body className="d-flex justify-content-between align-items-center gap-3">
                                    <div>
                                        <div className="fw-semibold">
                                            {providerLabels[p.provider] || p.provider}
                                            {p.active && <Badge bg="primary" className="ms-2">Активна</Badge>}
                                        </div>
                                        <code className="muted-small">{p.model}</code>
                                        {!p.configured && (
                                            <div className="text-danger muted-small">
                                                <i className="bi bi-exclamation-triangle me-1" />
                                                API-ключ не настроен на сервере
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        size="sm"
                                        variant={p.active ? 'outline-secondary' : 'primary'}
                                        disabled={p.active || !p.configured || saving !== null}
                                        onClick={() => select(p.provider)}
                                    >
                                        {saving === p.provider
                                            ? <Spinner animation="border" size="sm" />
                                            : p.active ? 'Используется' : 'Выбрать'}
                                    </Button>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default AiProviderCard;
